import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faSpinner, faCloudUploadAlt, faImage, faVideo, faFile, faCheck, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const UploadMedia = ({ darkMode, onClose, onMediaUploaded }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        mediaType: 'image',
        file: null,
        url: ''
    });
    const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    const mediaTypes = [
        { value: 'image', label: 'Image', icon: faImage },
        { value: 'video', label: 'Video', icon: faVideo },
        { value: 'document', label: 'Document', icon: faFile }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If URL is being entered, create preview for images
        if (name === 'url' && value && formData.mediaType === 'image') {
            setPreviewUrl(value);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, file }));
            
            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => setPreviewUrl(e.target.result);
                reader.readAsDataURL(file);
            } else {
                setPreviewUrl('');
            }
        }
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return false;
        }

        if (uploadMethod === 'file') {
            if (!formData.file) {
                toast.error('Please select a file to upload');
                return false;
            }
            
            // File size validation (50MB limit)
            if (formData.file.size > 50 * 1024 * 1024) {
                toast.error('File size must be less than 50MB');
                return false;
            }
        } else {
            if (!formData.url.trim()) {
                toast.error('Please enter a valid URL');
                return false;
            }
            
            // Basic URL validation
            try {
                new URL(formData.url);
            } catch {
                toast.error('Please enter a valid URL');
                return false;
            }
        }

        return true;
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            mediaType: 'image',
            file: null,
            url: ''
        });
        setUploadMethod('file');
        setPreviewUrl('');
        setUploadProgress(0);
        setUploadSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            let response;

            if (uploadMethod === 'file') {
                // File upload
                const submitData = new FormData();
                submitData.append('title', formData.title.trim());
                submitData.append('description', formData.description.trim());
                submitData.append('mediaType', formData.mediaType);
                submitData.append('media', formData.file);

                response = await api.post('/media/upload', submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(progress);
                    }
                });
            } else {
                // URL upload
                const submitData = {
                    title: formData.title.trim(),
                    description: formData.description.trim(),
                    mediaType: formData.mediaType,
                    url: formData.url.trim()
                };

                console.log('Sending URL upload request:', submitData);

                response = await api.post('/media/upload-url', submitData, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                
                console.log('URL upload response:', response.data);
                setUploadProgress(100);
            }

            if (response.data.success) {
                setUploadSuccess(true);
                toast.success('Media uploaded successfully!');
                onMediaUploaded && onMediaUploaded(response.data.media);
                
                // Reset form and prepare for next upload
                resetForm();
                
                // Show success message for a moment before auto-closing
                setTimeout(() => {
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error('Upload error:', error);
            console.error('Error response:', error.response?.data);
            
            const errorMessage = error.response?.data?.message || 
                                error.response?.data?.error || 
                                'Failed to upload media';
            
            toast.error(errorMessage);
            
            // Show detailed error in development
            if (import.meta.env.DEV) {
                console.error('Detailed error:', error.response?.data?.details);
            }
        } finally {
            setIsUploading(false);
        }
    };


    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Upload Media
                </h2>
            </div>

            {/* Form Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Media Type Selection */}
                    <div>
                        <label className="block text-sm font-medium mb-3">Media Type</label>
                        <div className="grid grid-cols-3 gap-3">
                            {mediaTypes.map((type) => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, mediaType: type.value }))}
                                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                        formData.mediaType === type.value
                                            ? darkMode
                                                ? 'border-blue-500 bg-blue-900/20 text-blue-300'
                                                : 'border-blue-500 bg-blue-50 text-blue-600'
                                            : darkMode
                                                ? 'border-gray-600 hover:border-gray-500 text-gray-300'
                                                : 'border-gray-300 hover:border-gray-400 text-gray-600'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={type.icon} className="text-xl" />
                                    <span className="text-sm font-medium">{type.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Upload Method Selection */}
                    <div>
                        <label className="block text-sm font-medium mb-3">Upload Method</label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setUploadMethod('file');
                                    setFormData(prev => ({ ...prev, url: '' }));
                                    setPreviewUrl('');
                                }}
                                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                    uploadMethod === 'file'
                                        ? darkMode
                                            ? 'border-green-500 bg-green-900/20 text-green-300'
                                            : 'border-green-500 bg-green-50 text-green-600'
                                        : darkMode
                                            ? 'border-gray-600 hover:border-gray-500 text-gray-300'
                                            : 'border-gray-300 hover:border-gray-400 text-gray-600'
                                }`}
                            >
                                <FontAwesomeIcon icon={faCloudUploadAlt} className="text-xl" />
                                <span className="text-sm font-medium">Upload File</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setUploadMethod('url');
                                    setFormData(prev => ({ ...prev, file: null }));
                                    setPreviewUrl('');
                                }}
                                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                    uploadMethod === 'url'
                                        ? darkMode
                                            ? 'border-purple-500 bg-purple-900/20 text-purple-300'
                                            : 'border-purple-500 bg-purple-50 text-purple-600'
                                        : darkMode
                                            ? 'border-gray-600 hover:border-gray-500 text-gray-300'
                                            : 'border-gray-300 hover:border-gray-400 text-gray-600'
                                }`}
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xl" />
                                <span className="text-sm font-medium">From URL</span>
                            </button>
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Title *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className={`w-full p-3 rounded-lg border ${
                                darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                    : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            placeholder="Enter media title"
                            required
                            disabled={isUploading}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            className={`w-full p-3 rounded-lg border ${
                                darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                    : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none`}
                            placeholder="Enter media description (optional)"
                            disabled={isUploading}
                        />
                    </div>

                    {/* File Upload or URL Input */}
                    {uploadMethod === 'file' ? (
                        <div>
                            <label className="block text-sm font-medium mb-2">Select File *</label>
                            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                                darkMode 
                                    ? 'border-gray-600 hover:border-gray-500' 
                                    : 'border-gray-300 hover:border-gray-400'
                            } transition-colors`}>
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="file-upload"
                                    accept={
                                        formData.mediaType === 'image' ? 'image/*' :
                                        formData.mediaType === 'video' ? 'video/*' :
                                        '*/*'
                                    }
                                    disabled={isUploading}
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    <FontAwesomeIcon 
                                        icon={faCloudUploadAlt} 
                                        className="text-4xl mb-4 text-gray-400" 
                                    />
                                    <p className="text-lg font-medium mb-2">
                                        {formData.file ? formData.file.name : 'Click to upload or drag and drop'}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {formData.mediaType === 'image' && 'PNG, JPG, GIF up to 50MB'}
                                        {formData.mediaType === 'video' && 'MP4, MOV, AVI up to 50MB'}
                                        {formData.mediaType === 'document' && 'PDF, DOC, TXT up to 50MB'}
                                    </p>
                                    {formData.file && (
                                        <p className="text-sm mt-2 text-blue-500">
                                            Size: {formatFileSize(formData.file.size)}
                                        </p>
                                    )}
                                </label>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium mb-2">Media URL *</label>
                            <input
                                type="url"
                                name="url"
                                value={formData.url}
                                onChange={handleInputChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                placeholder={
                                    formData.mediaType === 'image' ? 'https://example.com/image.jpg' :
                                    formData.mediaType === 'video' ? 'https://example.com/video.mp4' :
                                    'https://example.com/document.pdf'
                                }
                                required
                                disabled={isUploading}
                            />
                            <p className="text-sm text-gray-500 mt-2">
                                Enter a direct URL to the {formData.mediaType} file
                            </p>
                        </div>
                    )}

                    {/* Image Preview */}
                    {previewUrl && (
                        <div>
                            <label className="block text-sm font-medium mb-2">Preview</label>
                            <div className="rounded-lg overflow-hidden border border-gray-600/30">
                                <img 
                                    src={previewUrl} 
                                    alt="Preview" 
                                    className="w-full h-48 object-cover"
                                />
                            </div>
                        </div>
                    )}

                    {/* Upload Progress */}
                    {isUploading && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">Uploading...</span>
                                <span className="text-sm text-gray-500">{uploadProgress}%</span>
                            </div>
                            <div className={`w-full bg-gray-200 rounded-full h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {uploadSuccess && (
                        <div className="flex items-center justify-center p-4 bg-green-100 dark:bg-green-900/20 rounded-lg">
                            <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2" />
                            <span className="text-green-700 dark:text-green-300 font-medium">
                                Media uploaded successfully!
                            </span>
                        </div>
                    )}

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t border-gray-600/30">
                        <button
                            type="submit"
                            disabled={isUploading || uploadSuccess}
                            className={`px-6 py-3 rounded-lg font-medium text-white ${
                                darkMode 
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2`}
                        >
                            {isUploading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                            {isUploading 
                                ? (uploadMethod === 'file' ? 'Uploading...' : 'Adding Media...') 
                                : uploadSuccess 
                                    ? 'Added!' 
                                    : (uploadMethod === 'file' ? 'Upload Media' : 'Add Media from URL')
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

UploadMedia.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onMediaUploaded: PropTypes.func
};

export default UploadMedia;
